import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, forkJoin, from, map, Observable, of, switchMap, take, tap, catchError } from 'rxjs';
import { DocumentByIdResponse, DocumentTypeOption, GetDocumentTypeByIdResponse, ProductionDocumentsListResponse, ReturnDocumentTypeIdentifiersResponse, TestDocumentsListResponse } from '../shared/components/signature-file-upload/file-upload.model';
import { PreDefinedTagApiRecord } from '../shared/components/pdf-xeditor/lib/const/issuer-predefined-tags.const';
import { LoaderService } from './loader.service';
import { ZstdBokuwebService } from './zsdt_bokuweb.service';
import { LogRocketService } from './logrocket.service';
import { ConfigService } from './config.service';
import { ButtonCooldownService } from './button-cooldown.service';
import { BrowserFingerprintService } from './browser-fingerprint.service';
import { AUTH_WEB_HANDOFF_PATH, AUTH_WEB_LOGOUT_PATH, AUTH_WEB_REFRESH_PATH } from '../http/auth-api-paths';
@Injectable({
    providedIn: 'root'
})

export class ApiService {
    private readonly loginPortalPath = '/usermgmt/loginportal';
    private readonly authRefreshPath = AUTH_WEB_REFRESH_PATH;
    private readonly authHandoffPath = AUTH_WEB_HANDOFF_PATH;
    private readonly authWebLogoutPath = AUTH_WEB_LOGOUT_PATH;
    private readonly returnRoleByUserNamePath = '/usermgmt/returnRoleByUserName';

    constructor(private http: HttpClient, private loader: LoaderService,
        private zstdBokuwebCompressor: ZstdBokuwebService,
        private logRocket: LogRocketService,
        private configService: ConfigService,
        private buttonCooldown: ButtonCooldownService,
        private browserFingerprint: BrowserFingerprintService,
    ) {}

    private get baseUrl(): string {
        return this.configService.config.BASE_URL;
    }

    getAuthRefreshPath(): string {
        return this.authRefreshPath;
    }

    getAuthRefreshUrl(): string {
        return `${this.baseUrl.replace(/\/$/, '')}${this.authRefreshPath}`;
    }

    getAuthHandoffUrl(): string {
        return `${this.baseUrl.replace(/\/$/, '')}${this.authHandoffPath}`;
    }

    getLoginPortalUrl(): string {
        return `${this.baseUrl.replace(/\/$/, '')}${this.loginPortalPath}`;
    }

    getReturnRoleByUserNameUrl(): string {
        return `${this.baseUrl.replace(/\/$/, '')}${this.returnRoleByUserNamePath}`;
    }

    createOrg(req): Observable<any[]> {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/createOrganization`, req, { 'headers': headers });


    }

    checkOrganization(req: any) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/checkOrganization`, req, { 'headers': headers });
    }
    validateEmail(req: any) {
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/validateEmail`, req)
    }

    validatePublicEmail(req: any) {
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/validatePublicEmail`, req)
    }

    validateMobile(req: any) {
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/validateMobileNumber`, req)
    }

    validateName(req: any) {
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/validateName`, req)
    }

    getCountriesWithCode() {
        const headers = { 'content-type': 'application/json' };
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnCountries`, { 'headers': headers }).pipe(
            catchError(() => of(null)),
        );
    }

    getOmniCountries() {
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnOmniCountries`)
    }

    getOmniPrices(country_id: any) {
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/returnOmniPrices`, country_id)
    }
    getSuggestedDomain(organization: any) {
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/returnSuggestedDomainContent`, organization)
    }

    getUserCountry() {
        return this.http.get<any>("https://geolocation-db.com/json/")
    }

    getBusinessSector(): Observable<any[]> {
        // return this.bussinessSectorList
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/returnBusinessSectors`, { 'headers': headers })
    }

    // /indvonboard/returnIdType
    returnIdType(): Observable<any[]> {
        // return this.bussinessSectorList
        const headers = { 'content-type': 'application/json' };
        return this.http.get<any>(`${this.baseUrl}/indvonboard/returnIdType`, { 'headers': headers })
    }
    onLogin(req: any) {
        this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(this.getLoginPortalUrl(), req, { 'headers': headers });
    }

    onLogout(req: Record<string, unknown> = {}) {
        this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(
            `${this.baseUrl.replace(/\/$/, '')}${this.authWebLogoutPath}`,
            req,
            { 'headers': headers },
        )
        .pipe(
            tap(() => this.buttonCooldown.clearAll()),
            finalize(() => { this.logRocket.identify(); }),
        );
    }

    checkForgotCredentials(req: { email: string; recaptcha_key: string }) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/checkForgotCredentials`, req, { 'headers': headers });
    }

    onForgotPassword(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/forgotCredentials`, req, { 'headers': headers });
    }

    checkChangePassword(req: {
        old_password: string;
        confirm_new_password: string;
        new_password: string;
        recaptcha_key: string;
    }) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/checkChangePassword`, req, { 'headers': headers });
    }

    onChangePassword(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/changePassword`, req, { 'headers': headers });
    }

    checkResetLinkExpiry(req: { ref: string }) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/checkResetLinkExpiry`, req, { 'headers': headers });
    }

    checkResetPassword(req: {
        password: string;
        confirm_password: string;
        recaptcha_key: string;
        ref?: string;
    }) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/checkResetPassword`, req, { 'headers': headers });
    }

    onResetPassword(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/resetPassword`, req, { 'headers': headers });
    }

    // Post : https://20.74.45.220/dev/usermgmt/resetPasswordInternal
    resetPasswordInternal(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/resetPasswordInternal`, req, { 'headers': headers });
    }

    // ohttps://20.74.45.220/usermgmt/returnRoleByUserName

    getMenuList() {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.get<any>(this.getReturnRoleByUserNameUrl(), { 'headers': headers });
    }

    uploadDocument(req: any) {
        const doc1 = req?.document_list?.[0] ?? null;
        const doc2 = req?.digital_signature?.signatureDocuments?.documentContent ?? null;

        const compressDoc1$ = doc1
            ? from(this.zstdBokuwebCompressor.compressBase64ToCompressedBase64(doc1, 3))
            : of(null);

        const compressDoc2$ = doc2
            ? from(this.zstdBokuwebCompressor.compressBase64ToCompressedBase64(doc2, 3))
            : of(null);

        return forkJoin([compressDoc1$, compressDoc2$]).pipe(
            switchMap(([res1, res2]) => {
                let body = { ...req };

                if (res1) {
                    body = {...body,document_list: [res1.data]};
                }

                if (res2) {
                    body = {
                        ...body,
                        digital_signature: {
                            ...body.digital_signature,
                            signatureDocuments: {
                                ...body.digital_signature?.signatureDocuments,
                                documentContent: res2.data
                            }
                        }
                    };
                }

                const isCompressed =
                    (res1 && res1.compressed) || (res2 && res2.compressed);

            let headers = new HttpHeaders().set('Content-Type', 'application/json');

            if (isCompressed) {
                headers = headers.set('custom_compression', 'zstd');
            }

            return this.http.post<any>(`${this.baseUrl}/docmgmt/upload`, body, { headers });
            })
        );
    }
    // /search/document/globalSearch
    globalSearch(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/archiveservice/globalSearch`, req);
    }
    // getDocuments(pageNumber:any,pageSize:any) {
    //     //this.loader.show();
    //     return this.http.get<any>(`${this.baseUrl}/docmgmt/returnProductionUploadedDocuments?page_number=${pageNumber}&page_size=${pageSize}`);
    // }

    getDocuments(pageNumber: any, pageSize: any, filters: any) {
        //this.loader.show();

        let queryParams = `?page_number=${pageNumber}&page_size=${pageSize}`;

        if (filters) {
            Object.keys(filters).forEach(key => {
                queryParams += `&${key}=${encodeURIComponent(filters[key])}`;
            });
        }

        return this.http.get<ProductionDocumentsListResponse>(`${this.baseUrl}/docmgmt/returnProductionUploadedDocuments${queryParams}`);
    }

    returnDocuments() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnDocuments`);
    }

    /** docmgmt/returnDocumentTypeByOrganization — list of document types (type_code, type_description) for the organization. */
    getDocumentTypes() {
        return this.http.get<DocumentTypeOption[]>(`${this.baseUrl}/docmgmt/returnDocumentTypeByOrganization`);
    }

    getTypeDigitalCommunication(docTypeId) {
        return this.http.get<any>(`${this.baseUrl}/doctmgmt/returnDigitalCommunication?doc_type_id=${docTypeId}`);
    }
    // doctmgmt/returnAlternativeCommChannel
    returnAlternativeCommChannel() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/doctmgmt/returnAlternativeCommChannel`);
    }

    generateQrPreviewByDocTypeId(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/generateQrPreviewByDocTypeId`, req, { 'headers': headers });
    }

    testUploadDocument(req: any) {
        const doc1 = req?.document_list?.[0] ?? null;
        const doc2 = req?.digital_signature?.signatureDocuments?.documentContent ?? null;

        const compressDoc1$ = doc1
            ? from(this.zstdBokuwebCompressor.compressBase64ToCompressedBase64(doc1, 3))
            : of(null);

        const compressDoc2$ = doc2
            ? from(this.zstdBokuwebCompressor.compressBase64ToCompressedBase64(doc2, 3))
            : of(null);

        return forkJoin([compressDoc1$, compressDoc2$]).pipe(
            switchMap(([res1, res2]) => {
                let body = { ...req };

                if (res1) {
                    body = {...body,document_list: [res1.data]};
                }

                if (res2) {
                    body = {
                        ...body,
                        digital_signature: {
                            ...body.digital_signature,
                            signatureDocuments: {
                                ...body.digital_signature?.signatureDocuments,
                                documentContent: res2.data
                            }
                        }
                    };
                }

                const isCompressed =
                    (res1 && res1.compressed) || (res2 && res2.compressed);

                let headers = new HttpHeaders().set('Content-Type', 'application/json');

                if (isCompressed) {
                    headers = headers.set('custom_compression', 'zstd');
                }

                return this.http.post<any>(`${this.baseUrl}/docmgmt/testUpload`, body, { headers });
            })
        );
    }

    testGetDocuments(pageNumber: any, pageSize: any, filters: any) {
        //this.loader.show();

        let queryParams = `?page_number=${pageNumber}&page_size=${pageSize}`;

        if (filters) {
            Object.keys(filters).forEach(key => {
                queryParams += `&${key}=${encodeURIComponent(filters[key])}`;
            });
        }

        return this.http.get<TestDocumentsListResponse>(`${this.baseUrl}/docmgmt/returnTestUploadedDocuments${queryParams}`);
    }


    //configureOrganization
    configureOrganization(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/registerOrganization`, req);
    }


    getOrgnizations() {

        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnOrganizations`);
    }

    // https://20.74.45.220/orgmgmt/getOrganizationByUserName?user_name=dart

    getOrgByUserName() {
        ////this.loader.show();
        // ?user_name=${encodeURIComponent(user_name)
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/getOrganizationByUserName`);
    }

    updateAccount(req: any) {
        //this.loader.show();
        return this.http.put<any>(`${this.baseUrl}/orgmgmt/updateOrganization`, req);
    }

    // https://20.74.45.220/orgmgmt/getOrganizationById?id=165
    getOrgById(userId: string) {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/getOrganizationById?id=${userId}`);
    }

    approveOrganization(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/approveOrganization`, req);
    }

    updateOrgAdditionalDetails(req: any) {
        //this.loader.show();
        return this.http.put<any>(`${this.baseUrl}/orgmgmt/updateAdditionalDetails`, req);
    }

    //https://20.74.45.220/docmgmt/getDocumentById?id=8d5b32f9-2b3f-4b6b-ba73-02348777dc9d
    getDocById(docId: string): Observable<DocumentByIdResponse> {
        //this.loader.show();
        return this.http.get<DocumentByIdResponse>(
            `${this.baseUrl}/docmgmt/getDocumentById?id=${docId}` , { observe: 'response' }
        ).pipe(
            switchMap(res => {
                const body = res.body;
                const compression = res.headers.get('custom_compression');
                if ( compression !== 'zstd' || !body?.document_content ) {
                    return of(body);
                }

                return from(
                    this.zstdBokuwebCompressor.decompressBase64ToBase64(body.document_content)
                ).pipe(
                    map(decompressed => ({...body,document_content: decompressed}))
                );
            })
        );
}

    //https://20.74.45.220/dev/docmgmt/getTestDocumentById
    getTestDocById(docId: string): Observable<DocumentByIdResponse> {
        //this.loader.show();
        return this.http.get<DocumentByIdResponse>(
            `${this.baseUrl}/docmgmt/getTestDocumentById?id=${docId}`, { observe: 'response' }
        ).pipe(
            switchMap(res => {
                const body = res.body;
                const compression = res.headers.get('custom_compression');

                if ( compression !== 'zstd' || !body?.document_content ) {
                    return of(body);
                }

                return from(
                    this.zstdBokuwebCompressor.decompressBase64ToBase64(body.document_content)
                ).pipe(
                    map(decompressed => ({...body,document_content: decompressed}))
                );
            })
        );
    }



    getNotarization() {
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnNotarization`);
    }

    getStorageProvider() {
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnStorageProvider`);
    }
    getDocumentStorage() {

        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnDocumentStorage`);
    }

    returnOtpAuthentication() {
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnOtpAuthentication`);
    }

    returnOtpAuthenticationSignLink() {
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnOtpAuthenticationSignLink`);
    }

    getQRDesigns() {
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnQrDesigns`);
    }

    ///doctmgmt/returnConfiguration?org_id

    getConfigurationByOrgId() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/doctmgmt/returnConfiguration`);
    }

    generateQRPreview(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/docmgmt/generateQrPreview`, req);
    }

    /**
     * Create preview PDF (e.g. fix fonts / compression).
     * POST body: { pdf_file: base64 string (ZSTD-compressed when effective) }
     * Returns JSON { pdf_file: base64 string }.
     * Response is always checked for ZSTD (header or KLUv prefix), independent of request compression.
     */
    createPreviewPdf(req: { pdf_file: string; reference?: string }): Observable<{ pdf_file: string }> {
        const pdfBase64 = req?.pdf_file;
        const compressRequest$ = pdfBase64
            ? from(this.zstdBokuwebCompressor.compressBase64ToCompressedBase64(pdfBase64, 3))
            : of({ data: pdfBase64, compressed: false as const });

        return compressRequest$.pipe(
            switchMap((result) => {
                const body = pdfBase64 ? { ...req, pdf_file: result.data } : req;
                let headers = new HttpHeaders().set('content-type', 'application/json');
                if (result.compressed) {
                    headers = headers.set('custom_compression', 'zstd');
                }
                return this.http.post<{ pdf_file: string }>(`${this.baseUrl}/docmgmt/createPreviewPdf`, body, {
                    headers,
                    responseType: 'json',
                    observe: 'response',
                });
            }),
            switchMap((res) => this.handleCreatePreviewPdfResponse(res))
        );
    }

    private handleCreatePreviewPdfResponse(
        res: HttpResponse<{ pdf_file: string }>
    ): Observable<{ pdf_file: string }> {
        const body = res.body;
        const compression = res.headers.get('custom_compression');
        const pdfFile = body?.pdf_file || '';
        const looksLikeZstd = typeof pdfFile === 'string' && pdfFile.startsWith('KLUv');
        const shouldDecompress = (compression === 'zstd') || looksLikeZstd;

        if (!shouldDecompress || !body?.pdf_file) {
            return of(body as { pdf_file: string });
        }

        return from(this.zstdBokuwebCompressor.decompressBase64ToBase64(body.pdf_file)).pipe(
            map((decompressed) => ({ ...body, pdf_file: decompressed }))
        );
    }

    private handleDocumentContentResponse<T extends { document_content?: string; documentContent?: string }>(
        res: HttpResponse<T>
    ): Observable<T | null> {
        const body = res.body;
        if (!body) {
            return of(null);
        }

        const compression = res.headers.get('custom_compression');
        const content =
            (typeof body.document_content === 'string' && body.document_content) ||
            (typeof body.documentContent === 'string' && body.documentContent) ||
            '';
        const looksLikeZstd = content.startsWith('KLUv');
        const shouldDecompress = compression === 'zstd' || looksLikeZstd;

        if (!shouldDecompress || !content) {
            return of(body);
        }

        return from(this.zstdBokuwebCompressor.decompressBase64ToBase64(content)).pipe(
            map((decompressed) => {
                const next = { ...body };
                if (typeof body.document_content === 'string') {
                    next.document_content = decompressed;
                }
                if (typeof body.documentContent === 'string') {
                    next.documentContent = decompressed;
                }
                return next;
            })
        );
    }

    ///doctmgmt/returnDocumentTypes?org_id=165

    getDocumentTypesByOrg(orgId: any, pageNumber: any, pageSize: any, filters: any) {
        //this.loader.show();
        let url = `${this.baseUrl}/doctmgmt/returnDocumentTypes?page_number=${pageNumber}&page_size=${pageSize}`;

        if (orgId) {
            url += `&org_id=${orgId}`;
        }

        if (filters) {
            for (const key in filters) {

                if (filters[key] !== undefined && filters[key] !== null) {
                    url += `&${encodeURIComponent(key)}=${encodeURIComponent(filters[key])}`;
                }
            }
        }

        return this.http.get<any>(url);
    }

    getDocumentTypeById(dTypeId: number): Observable<GetDocumentTypeByIdResponse> {
        return this.http.get<GetDocumentTypeByIdResponse>(`${this.baseUrl}/doctmgmt/returnDocumentTypeById?doc_type_id=${dTypeId}`);
    }

    /** doctmgmt/returnPreDefinedTags — issuer tag catalog. */
    returnPreDefinedTags(): Observable<PreDefinedTagApiRecord[]> {
        return this.http.get<PreDefinedTagApiRecord[]>(`${this.baseUrl}/doctmgmt/returnPreDefinedTags`);
    }

    /** doctmgmt/returnDocumentTypeIdentifiers */
    returnDocumentTypeIdentifiers(): Observable<ReturnDocumentTypeIdentifiersResponse> {
        return this.http.get<ReturnDocumentTypeIdentifiersResponse>(`${this.baseUrl}/doctmgmt/returnDocumentTypeIdentifiers`);
    }

    //https://20.74.45.220/orgmgmt/configureOrganization

    configureOrg(req: any) {
        //this.loader.show();
        // return this.http.post<any>(`${this.baseUrl}/orgmgmt/configureOrganization`, req);
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/configureOrganization`, req);

    }

    getValidities() {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.get<any>(`${this.baseUrl}/doctmgmt/returnDocumentTypeValidity`, { 'headers': headers });
    }

    getPages() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/doctmgmt/returnPages`);
    }
    // doctmgmt/createDocumentType
    createDocumentType(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/doctmgmt/createDocumentType`, req);
    }

    updateDocumentType(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/doctmgmt/editDocumentType`, req);
    }

    //https://20.74.45.220/doctmgmt/deleteDocumentType
    deleteDocumentType(req: any) {
        //this.loader.show();
        const options = {
            body: {
                ...req
            },
        };
        return this.http.delete<any>(`${this.baseUrl}/doctmgmt/deleteDocumentType`, options);
    }

    //Delete : https://20.74.45.220/dev/docmgmt/delete
    deleteDocument(req: any) {
        //this.loader.show();
        return this.http.delete<any>(`${this.baseUrl}/docmgmt/delete`, req);
    }

    // (POST) https://20.74.45.220/dev/docmgmt/editDocument
    editDocument(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/editDocument`, req, { 'headers': headers });
    }

    // (POST) https://20.74.45.220/dev/docmgmt/editTestDocument
    editTestDocument(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/editTestDocument`, req, { 'headers': headers });
    }


    resendDocLink(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/docmgmt/regeneratePinCode`, req);
    }

    resendHistoryLink(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/resendHistoryLink`, req, { 'headers': headers });
    }

    resendByWhatsapp(req: any) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/resendByWhatsapp`, req, { 'headers': headers });
    }

    resendBySms(req: any) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/resendBySms`, req, { 'headers': headers });
    }

    resendByEmail(req: any) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/resendByEmail`, req, { 'headers': headers });
    }

    //Delete : https://20.74.45.220/dev/docmgmt/testDelete
    deleteTestDocument(req: any) {
        //this.loader.show();
        return this.http.delete<any>(`${this.baseUrl}/docmgmt/testDelete`, req);
    }

    // getUsers(pageNumber:any,pageSize:any) {
    //     
    //     //this.loader.show();
    //     return this.http.get<any>(`${this.baseUrl}/usermgmt/returnUsers?page_number=${pageNumber}&page_size=${pageSize}`);
    // }
    getUsers(pageNumber: number, pageSize: number, filters: any) {
        //this.loader.show();

        let filterParams = '';
        if (filters) {
            for (const key in filters) {
                if (filters[key]) {
                    filterParams += `&${key}=${encodeURIComponent(filters[key])}`;
                }
            }
        }

        const url = `${this.baseUrl}/usermgmt/returnUsers?page_number=${pageNumber}&page_size=${pageSize}${filterParams}`;
        return this.http.get<any>(url);
    }


    getRoles() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/usermgmt/returnRoles`);
    }

    createUser(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/usermgmt/createUser`, req);
    }

    updateUser(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/usermgmt/updateUser`, req);
    }

    docTReactive(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/doctmgmt/reactivateDocumentType`, req);
    }

    docTSuspend(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/doctmgmt/suspendDocumentType `, req);
    }

    getUserDetails(user_name: string) {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/usermgmt/returnUserDetails?user_name=${encodeURIComponent(user_name)}`);
    }

    getDefaultConfig() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/getOrganizationByUserName`);
    }

    userReactive(req: any) {
        //this.loader.show();


        return this.http.post<any>(`${this.baseUrl}/usermgmt/reactivateUser?user_name=${encodeURIComponent(req?.user_name)}`, req);
    }

    userActivityActionTypeList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/useractivitylog/activitytypes`);
    }

    userActivityLogs(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/useractivitylog/useractivityLogs`, req);
    }

    resendActivation(user_name: string) {
        // usermgmtdev/resendActivationLink?user_name=daronjava@hotmail.com
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/usermgmt/resendActivationLink?user_name=${encodeURIComponent(user_name)}`, {});
    }

    userSuspend(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/usermgmt/suspendUser?user_name=${encodeURIComponent(req?.user_name)}`, req);
    }

    orgSuspend(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/suspendOrganization`, req);
    }

    orgReactive(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/orgmgmt/reactivateOrganization`, req);
    }

    documentReactive(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/docmgmt/reactivateDocument`, req);
    }

    deleteUser(req: any) {
        //this.loader.show();
        const options = {
            body: {
                ...req
            },
        };
        return this.http.delete<any>(`${this.baseUrl}/usermgmt/deleteUser?user_name=${encodeURIComponent(req?.user_name)}`, options);
    }

    getRoleList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/rolesmgmt/returnCustomRoles`); //rolesmgmt - dev
    }

    getSystemRole() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/rolesmgmt/returnSystemRoles`);//rolesmgmt
    }

    getRoleDetails(role_id: any) {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/rolesmgmt/returnRoleById?role_id=${role_id}`);//rolesmgmt
    }

    checkDeleteRole(role_id: any) {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/rolesmgmt/checkDeleteRole?role_id=${role_id}`);//rolesmgmt
    }

    deleteRole(role_id: any) {
        //this.loader.show();
        return this.http.delete<any>(`${this.baseUrl}/rolesmgmt/deleteRole?role_id=${role_id}`);//rolesmgmt
    }

    getScreensByType(type_id) {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/rolesmgmt/returnScreensByType?type=${type_id}`);//rolesmgmt
    }

    createRole(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/rolesmgmt/createRole`, req);//rolesmgmt
    }

    updateRole(req: any) {
        //this.loader.show();
        return this.http.put<any>(`${this.baseUrl}/rolesmgmt/editRole`, req);
    }

    //https://20.74.45.220/dev/auditservice/screens
    screenList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/auditservice/screens`);
    }


    //https://20.74.45.220/dev/auditservice/actiontypes
    actionTypeList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/auditservice/actiontypes`);
    }

    //https://20.74.45.220/dev/docmgmt/returnStatuses
    returnStatusesList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnStatuses`);
    }

    //https://20.74.45.220/dev/docmgmt/returnDocumentTypes
    returnDocumentTypesList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnDocumentTypes`);
    }

    //https://20.74.45.220/dev/docmgmt/returnHolderNames
    holderNameList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnHolderNames`);
    }

    //https://20.74.45.220/dev/docmgmt/returnHolderIds
    returnHolderIdsList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnHolderIds`);
    }

    //https://20.74.45.220/dev/docmgmt/returnHolderEmail
    returnHolderEmailList() {
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnHolderEmail`);
    }

    //https://20.74.45.220/dev/docmgmt/returnDocumentNames
    returnDocumentNamesList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnDocumentNames`);
    }

    //https://20.74.45.220/dev/docmgmt//returnUploadedBy
    returnUploadedByList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnUploadedBy`);
    }

    returnUserList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/usermgmt/returnUsers`);
    }
    // https://20.74.45.220/qa/docmgmt/returnUsers
    returnUserManagementList() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnUsers`);
    }
    // customersupport/returnRequestType
    returnRequestType() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/customersupport/returnRequestType`);
    }
    // customersupport/createSupport
    createSupport(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/customersupport/createSupport`, req);
    }

    auditLogs(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/auditservice/auditLogs`, req);
    }

    returnSearchDocuments(req: any, pageNumber: number, pageSize: number, filters: any) {
        //this.loader.show();

        let queryParams = `?page_number=${pageNumber}&page_size=${pageSize}`;

        if (filters) {
            for (const key in filters) {
                if (filters[key]) {
                    queryParams += `&${encodeURIComponent(key)}=${encodeURIComponent(filters[key])}`;
                }
            }
        }

        const finalUrl = `${this.baseUrl}/docmgmt/returnSearchDocuments${queryParams}`;
        return this.http.post<any>(finalUrl, req);
    }


    //https://20.74.45.220/docmgmt/getDocumentById?id=8d5b32f9-2b3f-4b6b-ba73-02348777dc9d
    getDocumentManagementById(docId: string): Observable<any> {
        //this.loader.show();
        return this.http.get<any>(
            `${this.baseUrl}/docmgmt/getDocumentManagementById?id=${docId}`,
            { observe: 'response' }
        ).pipe(
            switchMap(res => {
                const body = res.body;
                const compression = res.headers.get('custom_compression');

                if (compression !== 'zstd' || !body?.document_content) {
                    return of(body);
                }

                return from(
                    this.zstdBokuwebCompressor.decompressBase64ToBase64(body.document_content)
                ).pipe(
                    map(decompressed => ({
                        ...body,
                        document_content: decompressed
                    }))
                );
            })
        );
    }

    /** Original (unmasked) PDF for print/download after masked viewer load. */
    downloadDocument(req: { document_reference: string; url_list?: string }): Observable<{ document_content: string; document_file_name?: string }> {
        const headers = {
            'content-type': 'application/json',
            custom_compression: 'zstd',
        };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/downloadDocument`, req, {
            headers,
            observe: 'response',
        }).pipe(
            switchMap((res) => {
                const body = res.body || {};
                const compression = res.headers.get('custom_compression');
                const content = body?.document_content;
                const looksLikeZstd = typeof content === 'string' && content.startsWith('KLUv');
                const shouldDecompress = compression === 'zstd' || looksLikeZstd;

                if (!shouldDecompress || !content) {
                    return of(body);
                }

                return from(this.zstdBokuwebCompressor.decompressBase64ToBase64(content)).pipe(
                    map((decompressed) => ({
                        ...body,
                        document_content: decompressed,
                    }))
                );
            })
        );
    }

    // (POST) https://20.74.45.220/dev/docmgmt/editDocumentManagement
    editDocumentManagement(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docmgmt/editDocumentManagement`, req, { 'headers': headers });
    }


    auditLogsDetails(trace_id: string) {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/auditservice/auditLogDetails?trace_id=${trace_id}`);
    }

    // returnRolesGrid(pageNumber:any,pageSize:any){
    //     //this.loader.show();
    //     return this.http.get<any>(`${this.baseUrl}/rolesmgmt/returnCustomRolesGrid?page_number=${pageNumber}&page_size=${pageSize}`); //rolesmgmt - dev
    // }
    returnRolesGrid(pageNumber: any, pageSize: any, filters?: any) {
        //this.loader.show();

        let query = `page_number=${pageNumber}&page_size=${pageSize}`;
        if (filters) {
            for (const key in filters) {
                if (filters[key]) {
                    query += `&${key}=${encodeURIComponent(filters[key])}`;
                }
            }
        }

        return this.http.get<any>(`${this.baseUrl}/rolesmgmt/returnCustomRolesGrid?${query}`);
    }

    getDocumentStatusData(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/highcharts/getDocumentsStatus`, req);
    }

    getDocumentsVolumeData(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/highcharts/getDocumentsVolume`, req);
    }

    getUserPerformanceData(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/highcharts/getUserPerformance`, req);
    }

    whatsappLogs(req: any) {
        //this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/communicationchannels/returnMessageLogs`, req);
    }

    getActualDatetime() {
        return this.http.get<any>(`https://worldtimeapi.org/api/ip`);
    }

    getArchiveLogs(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/archiveservice/globalSearch`, req, { 'headers': headers });
    }

    getDocument(reference_id: any) {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnDocumentById?id=${reference_id}`);
    }

  // docmgmt/returnMetaReference (GET)
  returnMetaReference() {
      //this.loader.show();
      return this.http.get<any>(`${this.baseUrl}/docmgmt/returnMetaReference`);
  }

    // docmgmt/returnMetatadatByDocumentTypeId — metadata for one or more document types
    getMetatadatByDocumentTypeId(doc_type_ids: any[] | any) {
        const document_type_ids = (Array.isArray(doc_type_ids) ? doc_type_ids : [doc_type_ids])
            .filter((id: any) => id != null && id !== '')
            .map((id: any) => {
                const numeric = Number(id);
                return Number.isFinite(numeric) ? numeric : id;
            });
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(
            `${this.baseUrl}/docmgmt/returnMetatadatByDocumentTypeId`,
            { document_type_ids },
            { 'headers': headers }
        );
    }


    getDocumentTypesByMultipleOrgs(orgIds: any) {
        //this.loader.show();
        var req = {
            "organization_id": orgIds
        }
        return this.http.post<any>(`${this.baseUrl}/docmgmt/returnListDocumentTypeByOrganizationIds`, req);
    }

    getUploadTypes() {
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnUploadType`);
    }

    getCommunicationChannels() {
        return this.http.get<any>(`${this.baseUrl}/docmgmt/returnCommunicationChannel`);
    }

    getActiveOrganizations() {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnActiveOrganizations`);
    }
    // /partners/approve?reference=...............
    getApproveOrg(reference: any) {
        return this.http.get<any>(`${this.baseUrl}/partners/approve?${reference}`);
    }


    getTranslationLabels(lang?) {
        let language
        if (lang == 'null' || lang == undefined) { language = 'en' } else { language = lang }
        const headers = { 'content-type': 'application/json' };
        return this.http.get<any>(`assets/json/translations-${language}.json`, {
            headers,
            withCredentials: false,
        }).pipe(
            catchError(() => of(null)),
        );
    }

    //   /usermgmt/changeLanguage (Post with token)
    changeLanguage(req: any) {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/changeLanguage`, req, { 'headers': headers });
    }


    getOrganizationStructure(orgId: any) {
        //this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/orgmgmt/returnOrganizationStructure?orgId=${orgId}`);
    }
    // usermgmt/getSearchAccessByUserName?user_name=daron19995@hotmail.com

    getSearchAccessByUserName(user_name: string) {
        ////this.loader.show();
        return this.http.get<any>(`${this.baseUrl}/usermgmt/getSearchAccessByUserName?user_name=${encodeURIComponent(user_name)}`);
    }
    //  /doctmgmt/returnQrGenerate (GET with token)
    returnQrGenerate() {
        // Do not call loader.show() here: /dashboard/document-type uses a custom local
        // loader and skips interceptor auto-hide. A leftover global true + BehaviorSubject
        // would hang the Document Template .page-load when file-upload-dtype mounts.
        const headers = { 'content-type': 'application/json' };
        return this.http.get<any>(`${this.baseUrl}/doctmgmt/returnQrGenerate`, { 'headers': headers });
    }

    createIndividual(req): Observable<any[]> {
        //this.loader.show();
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/indvonboard/createIndividual`, req, { 'headers': headers });
    }

     createExternalUser(req): Observable<any[]> {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/indvonboard/createExternalUser`, req, { 'headers': headers });
    }

    updateExternalUser(req): Observable<any[]> {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/indvonboard/updateIndividual`, req, { 'headers': headers });
    }

    sendOTP(identifier: any) {
        let query = '';

        if (typeof identifier === 'object' && identifier != null) {
            if (identifier.session_id != null && identifier.session_id !== '') {
                query = `mobile_number=${encodeURIComponent(String(identifier.session_id))}`;
            } else if (identifier.mobile_number != null && identifier.mobile_number !== '') {
                query = `mobile_number=${encodeURIComponent(String(identifier.mobile_number))}`;
                if (identifier.ref != null && identifier.ref !== '') {
                    query += `&ref=${encodeURIComponent(String(identifier.ref))}`;
                }
            } else {
                query = `mobile_number=${encodeURIComponent(String(identifier))}`;
            }
        } else {
            query = `mobile_number=${encodeURIComponent(String(identifier))}`;
        }

        return this.http.get<any>(`${this.baseUrl}/indvonboard/sendOtp?${query}`);
    }

    checkLogin(req: Record<string, unknown>) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/usermgmt/checkLogin`, req, { headers });
    }
    // usermgmt/loginIndividual  
    onIndividualLogin(req: any) {
        const headers = { 'content-type': 'application/json' };
// req ={
//     user_name: "Mysaa.Alrahal6@almasar-solutions.com",
//     password: "dart@T1234",
//     recaptcha_key:"recaptcha_key"
// }

        return this.http.post<any>(`${this.baseUrl}/usermgmt/loginIndividual`, req, { 'headers': headers });
    }
    // /indvonboard/returnIndividualById?email= 
    returnIndividualById(email: any) {
        return this.http.get<any>(`${this.baseUrl}/indvonboard/returnIndividualById?email=${encodeURIComponent(email)}`);
    }
    // indvonboard/isRegistered?email=
    isRegistered(email: any) {
          const headers = { 'content-type': 'application/json' };
        return this.http.get<any>(`${this.baseUrl}/indvonboard/isRegistered?email=${encodeURIComponent(email)}`, { 'headers': headers });
    }
    
    checkIndividual(req: any) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/indvonboard/checkIndividual`, req, { 'headers': headers });
    }

    getDefaultAnnotation(type,email) {
        return this.http.get<any>(`${this.baseUrl}/digitalsign/returnSignaturesByType?typeId=${type}&email=${encodeURIComponent(email)}`);
    }

    deleteDefaultSignature(req): Observable<any[]> {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/digitalsign/deleteSignature`, req, { 'headers': headers });
    }


    saveDefaultSignature(req): Observable<any[]> {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/digitalsign/saveSignature`, req, { 'headers': headers });
    }

    submitSignedPDFDocument(req): Observable<any[]> {
        const pdfBase64 = req?.signatureDocuments?.documentContent;
        if (!pdfBase64) {
            const headers = { 'content-type': 'application/json' };
            return this.http.post<any>(`${this.baseUrl}/digitalsign/uploadDocument`, req, { headers });
        }

        return from(
            this.zstdBokuwebCompressor.compressBase64ToCompressedBase64(pdfBase64, 3)
        ).pipe(
            switchMap((result) => {
                const body = {
                    ...req,
                    signatureDocuments: {
                        ...req.signatureDocuments,
                        documentContent: result.data,
                    },
                };
                let headers = new HttpHeaders().set('content-type', 'application/json');
                if (result.compressed) {
                    headers = headers.set('custom_compression', 'zstd');
                }
                return this.http.post<any>(`${this.baseUrl}/digitalsign/uploadDocument`, body, { headers });
            })
        );
    }

    updateSignedPDFDocument(req): Observable<any[]> {
        const pdfBase64 = req?.documentContent;
        if (!pdfBase64) {
            const headers = { 'content-type': 'application/json' };
            return this.http.post<any>(`${this.baseUrl}/digitalsign/updateDocument`, req, { headers });
        }

        return from(
            this.zstdBokuwebCompressor.compressBase64ToCompressedBase64(pdfBase64, 3)
        ).pipe(
            switchMap((result) => {
                const body = { ...req, documentContent: result.data };
                let headers = new HttpHeaders().set('content-type', 'application/json');
                if (result.compressed) {
                    headers = headers.set('custom_compression', 'zstd');
                }
                return this.http.post<any>(`${this.baseUrl}/digitalsign/updateDocument`, body, { headers });
            })
        );
    }

      getUserInfo(email:string) {
        return this.http.get<any>(`${this.baseUrl}/indvonboard/getIndividualById?email=${encodeURIComponent(email)}`);
    }

    checkSignatureDocument(req: { reference: string }): Observable<any> {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docscan/checkSignatureDocument`, req, { headers });
    }

      getSignatureDocument(req): Observable<any> {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/docscan/getSignatureDocument`, req, {
            headers,
            observe: 'response',
        }).pipe(
            switchMap((res) => this.handleDocumentContentResponse(res))
        );
    }

        getSignersOnDocumentId(req): Observable<any[]> {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/digitalsign/getSigners`, req, { 'headers': headers });
    }
    
    returnSignatureDocumentById(docId: string) {
        // this.loader.show();
        return this.http.get<any>(
            `${this.baseUrl}/digitalsign/returnSignatureDocumentById?id=${docId}`,
            { observe: 'response' }
        ).pipe(
            switchMap((res) => this.handleDocumentContentResponse(res))
        );
    }
    
    returnSignatureDocuments(pageNumber: any, pageSize: any, filters: any) {
        // this.loader.show();

        let queryParams = `?page_number=${pageNumber}&page_size=${pageSize}`;

        if (filters) {
            Object.keys(filters).forEach(key => {
                queryParams += `&${key}=${encodeURIComponent(filters[key])}`;
            });
        }

        return this.http.get<any>(`${this.baseUrl}/digitalsign/returnSignatureDocuments${queryParams}`);
    }


    resendDocument(req: any) {
        this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/digitalsign/resendDocument`, req);
    }
    
    resendlink(req: any) {
        this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/digitalsign/resendDocumentLink`, req);
    }
     cancelDocument(req: any) {
        this.loader.show();
        return this.http.post<any>(`${this.baseUrl}/digitalsign/cancelDocument`, req);
    }

    declineDocument(req: any) {
        const headers = { 'content-type': 'application/json' };
        return this.http.post<any>(`${this.baseUrl}/digitalsign/declineDocument`, req, { headers });
    }

    checkPDFOnLocalhostAPI(LOCALHOST_URL:string,req:any){
      return this.http.post<any>(`${LOCALHOST_URL}/signingPDF`, req);
    }

    pingLocalhostAPI(LOCALHOST_URL:string){
      return this.http.get<any>(`${LOCALHOST_URL}/ping`, { withCredentials: false });
    }

    downloadZip(url: string) {
    return this.http.get(url, { responseType: 'blob' });
    
}
    checkConfigOnLocalhost(LOCALHOST_URL:string) {
      return this.http.get<any>(`${LOCALHOST_URL}/checkConfig`, { withCredentials: false });
    }
    // docmgmt/returnOcrMetadata
    //   returnOcrMetadata(req: any) {
    //     this.loader.show();
    //     return this.http.post<any>(`${this.baseUrl}/docmgmt/returnOcrMetadata`, req);
    // }

    returnOcrMetadata(req: any) {
  return this.http.post(
    `${this.baseUrl}/docmgmt/returnOcrMetadata`,
    req,
    {
      responseType: 'blob' 
    }
  );
}

    
    parseDocument(req: any) {
        this.loader.show();
        return from(
            this.zstdBokuwebCompressor.compressBase64ToCompressedBase64(req.document_content, 3)
        ).pipe(
            switchMap((result) => {
                const body = { ...req, document_content: result.data };
                let headers = new HttpHeaders();

                if (result.compressed) {
                    headers = headers.set('custom_compression', 'zstd');
                }

                return this.http.post<any>(`${this.baseUrl}/docmgmt/parseDocument`, body, { headers }
                );
            })
        );
    }

    /**
       * Finalizes deletion. 
       * @param ids The IDs to remove.
       * @param useBeacon If true, uses a high-reliability transport for app exit/destruction.
       */
    public finalizeDeletion(ids: number[], useBeacon: boolean = false): void {
        if (!ids?.length) return;

        const req = { idList: [...ids] };

        if (useBeacon) {
            const fp = this.browserFingerprint.getCached();
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };
            if (fp) {
                headers['X-Device-Fingerprint'] = fp;
            }
            fetch(`${this.baseUrl}/digitalsign/deleteSignature`, {
                method: 'POST',
                body: JSON.stringify(req),
                keepalive: true,
                credentials: 'include',
                headers,
            }).catch(err => console.error('Background cleanup failed', err));

        } else {
            // THE "STANDARD" STRATEGY
            this.deleteDefaultSignature(req)
                .pipe(take(1))
                .subscribe({
                    next: () => console.log('Cleanup successful', req)
                });
        }
    }
}
